/**
 * Resultado da compressao de uma imagem escolhida pelo usuario.
 */
export interface ImagemComprimida {
    /** Data URL completa (`data:image/jpeg;base64,...`) — usada no preview. */
    dataUrl: string;
    /** Base64 sem o prefixo — o que vai no corpo da requisicao. */
    base64: string;
}

/**
 * Compressao de imagens no proprio navegador, antes do envio em Base64.
 *
 * Espelha o comportamento do app mobile (`imagem_controller.dart`): tenta a
 * escada 70/1024 -> 50/800 -> 30/600 e para na primeira tentativa que couber
 * em 2 MB. Sem isso o site envia o arquivo original inteiro (uma foto de
 * celular pode passar de 5 MB, que ainda incham ~33% na codificacao Base64).
 *
 * A saida e sempre JPEG porque o backend grava toda imagem como `.jpg`
 * (`ImagemService.salvarImagemBase64`) — converter aqui nao muda o resultado
 * final e evita o trafego desnecessario.
 */
export class ImagemUtils {

    /** Limite por imagem, igual ao usado no mobile. */
    private static readonly TAMANHO_MAXIMO_BYTES = 2 * 1024 * 1024;

    /**
     * Degraus de compressao, na mesma ordem do mobile.
     *
     * `lado` segue a semantica do `flutter_image_compress`: a imagem e reduzida
     * proporcionalmente ate que o MENOR lado alcance esse valor. Imagens ja
     * menores que isso nunca sao ampliadas.
     */
    private static readonly TENTATIVAS = [
        { qualidade: 0.7, lado: 1024 },
        { qualidade: 0.5, lado: 800 },
        { qualidade: 0.3, lado: 600 },
    ];

    /**
     * Comprime a imagem e devolve o preview e o Base64 prontos para uso.
     *
     * Se o navegador nao conseguir decodificar o arquivo, devolve o conteudo
     * original — o mesmo comportamento que o site tinha antes da compressao.
     */
    static async comprimirParaBase64(arquivo: File): Promise<ImagemComprimida> {
        try {
            const imagem = await this.carregarImagem(arquivo);
            let ultimaDataUrl = '';

            for (const tentativa of this.TENTATIVAS) {
                ultimaDataUrl = this.desenharEmJpeg(imagem, tentativa.lado, tentativa.qualidade);
                if (this.tamanhoEmBytes(ultimaDataUrl) <= this.TAMANHO_MAXIMO_BYTES) {
                    return this.montarResultado(ultimaDataUrl);
                }
            }

            // Nem o degrau mais agressivo coube: envia a menor versao gerada.
            return this.montarResultado(ultimaDataUrl);
        } catch {
            const dataUrl = await this.lerComoDataUrl(arquivo);
            return this.montarResultado(dataUrl);
        }
    }

    /** Remove o prefixo `data:image/...;base64,` de uma data URL. */
    static removerPrefixoDataUrl(dataUrl: string): string {
        const indice = dataUrl.indexOf('base64,');
        return indice >= 0 ? dataUrl.substring(indice + 'base64,'.length) : dataUrl;
    }

    /** Le o arquivo sem alterar nada (usado como fallback). */
    static lerComoDataUrl(arquivo: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const leitor = new FileReader();
            leitor.onload = () => resolve(leitor.result as string);
            leitor.onerror = reject;
            leitor.readAsDataURL(arquivo);
        });
    }

    private static montarResultado(dataUrl: string): ImagemComprimida {
        return { dataUrl, base64: this.removerPrefixoDataUrl(dataUrl) };
    }

    private static carregarImagem(arquivo: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const url = URL.createObjectURL(arquivo);
            const imagem = new Image();

            imagem.onload = () => {
                URL.revokeObjectURL(url);
                resolve(imagem);
            };
            imagem.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Nao foi possivel ler a imagem selecionada'));
            };

            imagem.src = url;
        });
    }

    /**
     * Desenha a imagem em um canvas reduzido e devolve a data URL em JPEG.
     */
    private static desenharEmJpeg(imagem: HTMLImageElement, lado: number, qualidade: number): string {
        const escala = Math.min(1, Math.max(lado / imagem.width, lado / imagem.height));
        const largura = Math.max(1, Math.round(imagem.width * escala));
        const altura = Math.max(1, Math.round(imagem.height * escala));

        const canvas = document.createElement('canvas');
        canvas.width = largura;
        canvas.height = altura;

        const contexto = canvas.getContext('2d');
        if (!contexto) {
            throw new Error('Canvas indisponivel neste navegador');
        }

        // JPEG nao suporta transparencia: sem o fundo branco o alpha vira preto.
        contexto.fillStyle = '#FFFFFF';
        contexto.fillRect(0, 0, largura, altura);
        contexto.imageSmoothingQuality = 'high';
        contexto.drawImage(imagem, 0, 0, largura, altura);

        return canvas.toDataURL('image/jpeg', qualidade);
    }

    /** Tamanho aproximado, em bytes, do conteudo Base64 de uma data URL. */
    private static tamanhoEmBytes(dataUrl: string): number {
        const base64 = this.removerPrefixoDataUrl(dataUrl);
        const preenchimento = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
        return Math.floor((base64.length * 3) / 4) - preenchimento;
    }
}
